/*
 * Maison Alia — SHARED CORE (TACBundleCore)
 * -----------------------------------------
 * Stable, line-agnostic infrastructure shared by every Maison Alia
 * configurator (Adult, Kids, and future lines). Contains NO business logic
 * (no composition, recommendations, matrices or line-specific state) — only
 * the bundle/cart contract and generic helpers.
 *
 * The grouped-cart REMOVE handler is the other half of this shared layer and
 * lives in layout/theme.liquid as a persistent document delegate; it is
 * unchanged and independent of any single configurator.
 *
 * Normalized line-item property contract (full operational schema; every field
 * is emitted only when provided, so it applies to Adult and Kids alike):
 *   Operational (customer-facing unless the grouped cart hides them):
 *     Set, Collection, Embroidery, Fabric, Size, Depth, Embroidery Tier,
 *     Personalization
 *   Internal (_ prefix, Admin-only):
 *     _bundle_id, _bundle_handle, _component_type, _line
 *   Clean omission: no Depth on non-Fitted pieces, no Embroidery Tier on the
 *   Fitted sheet, no Personalization when blank.
 *
 * UMD: browser global `TACBundleCore` + CommonJS for the offline harness.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.TACBundleCore = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---- Bundle identity ----------------------------------------------------
  function newBundleId(prefix) {
    var now = (typeof Date !== 'undefined' && Date.now) ? Date.now() : 0;
    var rnd = Math.random().toString(36).slice(2, 8);
    return (prefix || 'tac') + '-' + now + '-' + rnd;
  }

  // ---- Normalized property contract ---------------------------------------
  // Single source of truth for the line-item shape both configurators emit.
  // Emits every field PROVIDED, in a stable canonical order, so the full
  // operational/admin data is preserved on each line. The grouped cart decides
  // which of the visible (non-underscore) keys to surface to the customer;
  // underscore keys are hidden by Shopify automatically. Line-agnostic: it maps
  // input fields to keys and never assumes Kids or Adult.
  function buildProperties(o) {
    o = o || {};
    var p = {};
    // Visible / operational (customer-facing unless the grouped cart hides them)
    if (o.set) p.Set = o.set;
    if (o.collection) p.Collection = o.collection;
    if (o.embroidery) p.Embroidery = o.embroidery;
    if (o.fabric) p.Fabric = o.fabric;
    if (o.size) p.Size = o.size;
    if (o.depth) p.Depth = o.depth;
    if (o.tier) p['Embroidery Tier'] = o.tier;                 // tiered pieces only
    if (o.personalization) p.Personalization = o.personalization; // only when filled
    // Hidden (_ prefix, Admin-only)
    if (o.bundleId) p._bundle_id = o.bundleId;
    if (o.bundleHandle) p._bundle_handle = o.bundleHandle;
    if (o.componentType) p._component_type = o.componentType;
    if (o.line) p._line = o.line;                              // optional (e.g. "kids")
    return p;
  }

  // ---- Cart add + grouped-cart drawer (mirrors the validated Adult contract)
  // items: [{ id:Number, quantity:Number, properties:Object }]
  //
  // Mechanism, in order (matches the live Adult configurator, read-only ref):
  //   1. If window.__tacAddSet exists (Horizon @shopify/events add that
  //      dispatches CartLinesUpdateEvent + section refresh), use it.
  //   2. Otherwise do a section-aware AJAX add to Theme.routes.cart_add_url
  //      (falls back to /cart/add.js), passing the cart-items-component section
  //      ids so the drawer re-renders.
  //   3. Open the grouped-cart drawer via #cart-drawer.open() or the
  //      [aria-controls="cart-drawer"] trigger.
  // It never navigates to /cart — a failed drawer-open is surfaced via the
  // 'tac:cart:added' event, not a page load.
  function openCartDrawer() {
    if (typeof document === 'undefined') return false;
    var d = document.getElementById('cart-drawer');
    if (d && typeof d.open === 'function') { try { d.open(); return true; } catch (e) {} }
    var trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) { trigger.click(); return true; }
    return false;
  }

  function sectionIds() {
    if (typeof document === 'undefined') return [];
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (c) {
      if (c.dataset && c.dataset.sectionId) ids.push(c.dataset.sectionId);
    });
    return ids;
  }

  function addBundle(items) {
    var add;
    if (typeof window !== 'undefined' && typeof window.__tacAddSet === 'function') {
      add = window.__tacAddSet(items);           // exact Horizon events path
    } else {
      var addUrl = (typeof window !== 'undefined' && window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
      var body = { items: items.map(function (i) { return { id: Number(i.id), quantity: i.quantity, properties: i.properties }; }) };
      var ids = sectionIds();
      if (ids.length) body.sections = ids.join(',');
      add = fetch(addUrl, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(body)
      }).then(function (r) { return r.json(); }).then(function (res) {
        if (res && res.status) throw new Error(res.description || res.message || 'cart add failed');
        return res;
      });
    }
    return Promise.resolve(add).then(function (res) {
      if (typeof document !== 'undefined' && document.dispatchEvent)
        document.dispatchEvent(new CustomEvent('tac:cart:added', { detail: res }));
      openCartDrawer();                          // never navigates to /cart
      return res;
    });
  }

  // ---- Generic variant lookup ---------------------------------------------
  // group: { variantId: { fabric, size, opt3, price, available } }
  function findVariant(group, sel) {
    if (!group) return null;
    for (var id in group) {
      if (!Object.prototype.hasOwnProperty.call(group, id)) continue;
      var v = group[id];
      if (v.fabric === sel.fabric && v.size === sel.size && (sel.opt3 == null || v.opt3 === sel.opt3))
        return { id: id, price: v.price, available: v.available };
    }
    return null;
  }

  // ---- Money & measurement helpers ----------------------------------------
  function formatMoney(amount, currency) {
    try {
      var cur = currency || (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) || 'EUR';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(amount);
    } catch (e) { return String(amount); }
  }

  function cmToInches(cm, decimals) {
    var d = (decimals == null) ? 1 : decimals;      // inches to ONE decimal by default
    return Number((cm / 2.54).toFixed(d));
  }

  // Appends inches IN PLACE so any surrounding label is preserved, e.g.
  // "Kid — 30 × 40 cm" -> "Kid — 30 × 40 cm (11.8 × 15.7 in)".
  function formatMeasure(sizeStr, decimals) {
    return String(sizeStr).replace(/(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)\s*cm/i, function (_, w, h) {
      return w + ' × ' + h + ' cm (' + cmToInches(parseFloat(w), decimals) + ' × ' + cmToInches(parseFloat(h), decimals) + ' in)';
    });
  }

  // ---- Text normalization -------------------------------------------------
  // NFC so accented input compares/counts by composed code point; apostrophes
  // (straight ' and typographic ’ / ʼ) are preserved as typed.
  function normalizeText(s) {
    if (s == null) return '';
    return String(s).normalize ? String(s).normalize('NFC') : String(s);
  }

  return {
    newBundleId: newBundleId,
    buildProperties: buildProperties,
    addBundle: addBundle,
    openCartDrawer: openCartDrawer,
    findVariant: findVariant,
    formatMoney: formatMoney,
    cmToInches: cmToInches,
    formatMeasure: formatMeasure,
    normalizeText: normalizeText
  };
}));
